import { useEffect, useRef, useState, type FormEvent } from "react";
import "./onyxo-business-chat.css";

/**
 * First-party, zero-inference-cost ONYXO concierge.
 * No browsing, LLM, third-party transmission or automatic lead submission.
 * Visitor-initiated lead handoff goes to the site's existing form.
 */
export type OnyxoMode = "onyxweb" | "optimateo";
export type OnyxoLeadDraft = { topic: string; brief: string; service: string };
type Turn = { speaker: "visitor" | "onyxo"; content: string };
const TOPICS: Record<OnyxoMode, readonly { label: string; query: string }[]> = {
  onyxweb: [
    { label: "Nový web", query: "Potřebuji nový web, který přivede zákazníky" },
    { label: "Redesign", query: "Chci redesign současného webu" },
    { label: "Web + CRM", query: "Potřebuji web propojit s CRM a poptávkami" },
  ],
  optimateo: [
    { label: "Audit konverzí", query: "Chci audit konverzí a zjistit proč web nevydělává" },
    { label: "CRM a leady", query: "Chci automatizovat poptávky a CRM" },
    { label: "AI automatizace", query: "Zajímají mě AI agenti a automatizace podnikání" },
  ],
};
const norm=(s:string)=>s.normalize("NFKD").replace(/[\u0300-\u036f]/g,"").toLowerCase();
export function classifyOnyxoIntent(raw: string, mode: OnyxoMode): {topic:string;service:string;reply:string} {
  const q=norm(raw);
  if(/(?:cen|kolik|stoj|rozpocet|balicek)/.test(q)) return {
    topic:"pricing",service:mode==="onyxweb"?"web-design":"audit",
    reply:"Cena záleží na rozsahu, stavu současného webu a požadovaných integracích. Bez zadání nebudu tvrdit konkrétní částku ani návratnost. Připravme nezávaznou poptávku s cílem a rozpočtem, abychom mohli nabídnout přesnou cenu."
  };
  if(/(?:automatiz|crm|lead|poptav|agent|ai\b|mcp)/.test(q)) return {
    topic:"automation",service:"automation",
    reply:mode==="onyxweb"
      ?"Web můžeme propojit s poptávkovým formulářem, CRM a návaznými automatizacemi. Nejprve potřebujeme vědět, odkud přicházejí zákazníci a co se má stát po odeslání poptávky."
      :"Dává smysl začít jedním měřitelným procesem: získání leadu, jeho validace a předání do CRM. AI agent a MCP mohou navázat po ověření dat, práv a přístupů. Napište, která fáze dnes brzdí příjmy."
  };
  if(/(?:gastro|restaur|cajov|kavarn|rezervac|menu)/.test(q)) return {
    topic:"gastro",service:"gastro",
    reply:"U gastro provozu bych začal mobilním webem, přehledným menu a jednoduchou cestou k rezervaci nebo telefonátu. Návaznost na QR objednávky, CRM či pokladní systém doporučím až podle konkrétního provozu."
  };
  if(/(?:eshop|e-shop|objednav|nakup|prodej|plateb|checkout)/.test(q)) return {
    topic:"commerce",service:"ecommerce",
    reply:"Pro online prodej je zásadní cesta produkt → objednávka → platba → potvrzení. Nejdřív ověříme checkout, měření konverzí a reálné doručení; až potom optimalizujeme reklamu a další funkce."
  };
  if(/(?:audit|seo|rychlost|konverz|reklam|mereni|tracking)/.test(q)) return {
    topic:"audit",service:"audit",
    reply:"Začal bych rychlým auditem obchodních překážek: funkčnost formulářů, mobilní použitelnost, zdroje návštěvnosti a konverzní měření. Žádný růst ani ROI není možné slíbit bez skutečných dat."
  };
  if(/(?:redesign|design|vizual|vzhled|grafik)/.test(q)) return {
    topic:"redesign",service:"web-design",
    reply:"U redesignu navrhneme hierarchii, navigaci a CTA podle cílových zákazníků; vizuální směr musí podporovat srozumitelnost a konverze. Můžete mi poslat současný web a hlavní problém."
  };
  return {
    topic:"new-website",service:mode==="onyxweb"?"web-design":"audit",
    reply:mode==="onyxweb"
      ?"Pomohu vybrat rozsah nového webu nebo redesignu. Jaký typ podnikání máte a co má web přinést: poptávky, rezervace, nebo prodej?"
      :"Pomohu vytipovat nejbližší obchodní příležitost: audit webu, optimalizaci konverzí nebo automatizaci. Co dnes nejvíc blokuje vaše objednávky či poptávky?"
  };
}
export default function OnyxoBusinessChat({mode}: {mode:OnyxoMode}) {
  const [open,setOpen]=useState(false);
  const [input,setInput]=useState("");
  const [turns,setTurns]=useState<Turn[]>([]);
  const [draft,setDraft]=useState<OnyxoLeadDraft|null>(null);
  const logRef=useRef<HTMLDivElement>(null);
  const firstRef=useRef<HTMLInputElement>(null);
  useEffect(()=>{if(open)firstRef.current?.focus();},[open]);
  useEffect(()=>{if(open && logRef.current)logRef.current.scrollTop=logRef.current.scrollHeight;},[turns,open]);
  function send(text:string) {
    const q=text.trim().slice(0,500);
    if(!q)return;
    const result=classifyOnyxoIntent(q,mode);
    setTurns(v=>[...v,{speaker:"visitor" as const,content:q},{speaker:"onyxo" as const,content:result.reply}].slice(-24));
    setDraft({topic:result.topic,brief:q,service:result.service});
    setInput("");
  }
  function submit(e:FormEvent<HTMLFormElement>){e.preventDefault();send(input);}
  function handoff(){
    if(!draft)return;
    window.dispatchEvent(new CustomEvent<OnyxoLeadDraft>("onyxo:lead-draft",{detail:draft}));
    setOpen(false);
    const serviceQuiz=mode==="onyxweb" ? document.querySelector("form.onyx-lead-quiz") : null;
    if(serviceQuiz){serviceQuiz.scrollIntoView({behavior:"smooth",block:"start"});return;}
    const id=mode==="onyxweb"?"audit":"contact";
    if(document.getElementById(id))document.getElementById(id)?.scrollIntoView({behavior:"smooth",block:"start"});
    else window.location.assign(mode==="onyxweb"?"/#audit":"/#contact");
  }
  return <>
    {!open&&<button type="button" className="onyxo-business-open" onClick={()=>setOpen(true)}
      aria-label="Otevřít ONYXO – poradce">
      <span aria-hidden="true" className="onyxo-business-orb">✧</span>
    </button>}
    {open&&<section className="onyxo-business-panel" role="dialog" aria-modal="false" aria-label="ONYXO – poradce pro váš projekt">
      <header className="onyxo-business-header">
        <span className="onyxo-business-orb" aria-hidden="true">✧</span>
        <div><b>Asistent <span>{mode==="onyxweb"?"WEB STUDIO":"OPTIMATEO"}</span></b>
          <p>Průvodce službami · nezávazné doporučení</p></div>
        <button type="button" className="onyxo-business-close" onClick={()=>setOpen(false)} aria-label="Zavřít chat">×</button>
      </header>
      <div ref={logRef} className="onyxo-business-log" role="log" aria-live="polite" aria-relevant="additions">
        <p className="onyxo-business-bubble agent">
          Dobrý den! Jsem ONYXO. Pomohu vám ujasnit zadání a předat ho do nezávazné poptávky. Odpovídám z ověřeného rozsahu služeb; nejde o individuální cenovou nabídku.
        </p>
        {turns.map((msg,i)=><p key={i} className={"onyxo-business-bubble "+(msg.speaker==="visitor"?"visitor":"agent")}>{msg.content}</p>)}
        {draft&&<div className="onyxo-business-handoff">
          <p><b>Máme první zadání.</b> Zkontrolujete ho v poptávkovém formuláři, doplníte kontakt a sami rozhodnete o odeslání.</p>
          <button type="button" onClick={handoff}>Přejít k nezávazné poptávce ↗</button>
        </div>}
      </div>
      <div className="onyxo-business-compose">
        <div className="onyxo-business-chips">{TOPICS[mode].map(t=><button type="button" key={t.label} onClick={()=>send(t.query)}>{t.label}</button>)}</div>
        <form onSubmit={submit}>
          <label htmlFor={"onyxo-business-input-"+mode} className="onyxo-business-sr">Váš dotaz pro ONYXO</label>
          <input id={"onyxo-business-input-"+mode} ref={firstRef} value={input} onChange={e=>setInput(e.target.value)}
            placeholder="Co chcete vyřešit?" maxLength={500} autoComplete="off" />
          <button type="submit" disabled={!input.trim()} aria-label="Odeslat dotaz">↗</button>
        </form>
        <small>Konverzace není ukládána ani odesílána externím AI službám. Kontaktní údaje odešlete až ve formuláři.</small>
      </div>
    </section>}
  </>;
}
